import { useState, useEffect, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/cn";
import { CropMarks } from "@/components/common/CropMarks";
import { Reveal } from "@/components/common/Reveal";
import { useBackground } from "@/contexts/BackgroundContext";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface TabbedProductSectionProps {
  id: string;
  title: string;
  oneLiner: string;
  badge: { label: string; color: string };
  tractionLine: string;
  cta: { label: string; href: string; external?: boolean };
  secondaryCta?: { label: string; href: string; external?: boolean };
  features: Array<{ title: string; text: string }>;
  mockupStates: ReactNode[];
}

const PAD = (n: number) => String(n + 1).padStart(2, "0");
const CYCLE_MS = 5000;
const RESUME_MS = 8000;

export function TabbedProductSection({
  id,
  title,
  oneLiner,
  badge,
  tractionLine,
  cta,
  secondaryCta,
  features,
  mockupStates,
}: TabbedProductSectionProps) {
  const { overlayOpacity } = useBackground();
  const prefersReducedMotion = useReducedMotion();

  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const playing = visible && !userPaused && !prefersReducedMotion;

  // Progress bar reset key — increments on every tab change to restart CSS animation
  const [progressKey, setProgressKey] = useState(0);

  // Track visibility
  useEffect(() => {
    if (prefersReducedMotion) return;
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!!entry?.isIntersecting),
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  // Auto-cycle
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setActive((prev) => {
        const next = (prev + 1) % features.length;
        setProgressKey((k) => k + 1);
        return next;
      });
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [playing, features.length]);

  const handleTabClick = useCallback(
    (i: number) => {
      setActive(i);
      setProgressKey((k) => k + 1);
      setUserPaused(true);

      if (resumeTimer.current) clearTimeout(resumeTimer.current);
      if (!prefersReducedMotion) {
        resumeTimer.current = setTimeout(() => setUserPaused(false), RESUME_MS);
      }
    },
    [prefersReducedMotion],
  );

  const handleMouseEnter = useCallback(() => {
    if (prefersReducedMotion) return;
    setUserPaused(true);
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  }, [prefersReducedMotion]);

  const handleMouseLeave = useCallback(() => {
    if (prefersReducedMotion) return;
    resumeTimer.current = setTimeout(() => setUserPaused(false), RESUME_MS);
  }, [prefersReducedMotion]);

  // Cleanup resume timer
  useEffect(() => {
    return () => {
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
    };
  }, []);

  const overlayColor = `rgba(var(--color-overlay-rgb),${overlayOpacity})`;

  const tractionCta = (
    <>
      <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed mb-6 max-w-md">
        {tractionLine}
      </p>
      <div className="flex flex-wrap gap-3">
        {cta.external ? (
          <a
            href={cta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-5 py-2 text-sm font-[family-name:var(--font-mono)] border border-[var(--color-border)] hover:bg-[var(--color-surface)] transition-colors"
          >
            {cta.label}
          </a>
        ) : (
          <Link
            to={cta.href}
            className="inline-block px-5 py-2 text-sm font-[family-name:var(--font-mono)] border border-[var(--color-border)] hover:bg-[var(--color-surface)] transition-colors"
          >
            {cta.label}
          </Link>
        )}
        {secondaryCta && (
          secondaryCta.external ? (
            <a
              href={secondaryCta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-2 py-2 text-sm font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors"
            >
              {secondaryCta.label}
            </a>
          ) : (
            <Link
              to={secondaryCta.href}
              className="inline-flex items-center px-2 py-2 text-sm font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors"
            >
              {secondaryCta.label}
            </Link>
          )
        )}
      </div>
    </>
  );

  return (
    <section
      ref={sectionRef}
      id={id}
      className="relative z-10 backdrop-blur-[2px] py-24 px-8"
      style={{
        background: overlayColor,
        transition: "background 1.5s ease-in-out",
      }}
    >
      {/* Gradient fade-in */}
      <div
        className="absolute top-0 left-0 right-0 h-32 -translate-y-full pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, transparent, ${overlayColor})`,
          transition: "background 1.5s ease-in-out",
        }}
        aria-hidden="true"
      />

      <CropMarks />

      <div className="max-w-6xl mx-auto w-full">
        {/* Title + badge + one-liner */}
        <Reveal className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl md:text-5xl font-light tracking-tight font-[family-name:var(--font-display)]">
              {title}
            </h2>
            <span
              className={cn(
                "text-xs font-[family-name:var(--font-mono)] px-2 py-0.5 rounded",
                badge.color,
              )}
            >
              {badge.label}
            </span>
          </div>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-xl font-light">
            {oneLiner}
          </p>
        </Reveal>

        {/* Tab bar */}
        <Reveal delay={100}>
        <div
          ref={tabBarRef}
          className="relative flex overflow-x-auto md:overflow-visible border-b border-[var(--color-border)]"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          role="tablist"
        >
          {features.map((feat, i) => (
            <button
              key={feat.title}
              role="tab"
              aria-selected={active === i}
              onClick={() => handleTabClick(i)}
              className={cn(
                "relative flex-shrink-0 px-4 py-3 text-sm font-[family-name:var(--font-mono)] tracking-wider transition-colors cursor-pointer whitespace-nowrap",
                active === i
                  ? "text-[var(--color-fg)]"
                  : "text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-secondary)]",
              )}
            >
              {PAD(i)} · {feat.title}

              {/* Progress bar under active tab */}
              {active === i && (
                <span
                  key={progressKey}
                  className="absolute bottom-0 left-0 h-0.5 bg-[var(--color-fg)]"
                  style={
                    playing
                      ? {
                          width: "100%",
                          animation: `tab-progress ${CYCLE_MS}ms linear`,
                        }
                      : { width: "100%" }
                  }
                />
              )}
            </button>
          ))}
        </div>
        </Reveal>

        {/* Content area */}
        <Reveal delay={200}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 mt-6 md:mt-8">
          {/* Left column: feature text (crossfade) + static traction/CTA */}
          <div className="flex flex-col">
            <div className="relative min-h-[4rem]">
              {features.map((feat, i) => (
                <div
                  key={feat.title}
                  className={cn(
                    prefersReducedMotion
                      ? ""
                      : "transition-opacity duration-500 ease-out",
                    i === 0 ? "relative" : "absolute inset-0",
                  )}
                  style={{
                    opacity: active === i ? 1 : 0,
                    pointerEvents: active === i ? "auto" : "none",
                  }}
                  role="tabpanel"
                  aria-hidden={active !== i}
                >
                  <h3 className="text-xl font-medium mb-3 font-[family-name:var(--font-display)]">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed max-w-md">
                    {feat.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Traction + CTA — desktop only, pinned to bottom to align with mockup */}
            <div className="hidden md:block mt-auto pt-8">
              {tractionCta}
            </div>
          </div>

          {/* Mockup — crossfade */}
          <div className="relative">
            {mockupStates.map((state, i) => (
              <div
                key={i}
                className={cn(
                  prefersReducedMotion
                    ? ""
                    : "transition-all duration-500 ease-out",
                  i === 0 ? "relative" : "absolute inset-0",
                )}
                style={{
                  opacity: active === i ? 1 : 0,
                  transform:
                    prefersReducedMotion || active === i
                      ? "translateY(0)"
                      : "translateY(8px)",
                  pointerEvents: active === i ? "auto" : "none",
                }}
              >
                {state}
              </div>
            ))}
          </div>
        </div>

        </Reveal>

        {/* Traction + CTA — mobile only, below mockup */}
        <Reveal delay={300} className="md:hidden mt-8">
          {tractionCta}
        </Reveal>

      </div>

      {/* Progress bar keyframes */}
      <style>{`
        @keyframes tab-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
